import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  ReactNode,
} from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'success'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'data-testid'?: string;
}

export interface ButtonAsButtonProps
  extends CommonProps,
    Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      keyof CommonProps | 'aria-label' | 'aria-controls' | 'aria-expanded'
    > {
  as?: 'button';
  type?: 'button' | 'submit' | 'reset';
  href?: never;
}

export interface ButtonAsAnchorProps
  extends CommonProps,
    Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof CommonProps | 'aria-label' | 'aria-controls' | 'aria-expanded'
    > {
  as: 'a';
  href: string;
  type?: never;
}

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

function classes(
  variant: ButtonVariant,
  size: ButtonSize,
  iconOnly: boolean,
  fullWidth: boolean,
  loading: boolean,
  className?: string,
): string {
  return [
    'kk-button',
    `kk-button--${variant}`,
    `kk-button--${size}`,
    iconOnly ? 'kk-button--icon-only' : null,
    fullWidth ? 'kk-button--full-width' : null,
    loading ? 'kk-button--loading' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function Spinner(): JSX.Element {
  return <span className="kk-button__spinner" aria-hidden="true" />;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      fullWidth = false,
      children,
      className,
      'aria-label': ariaLabel,
      'aria-controls': ariaControls,
      'aria-expanded': ariaExpanded,
      'data-testid': dataTestId,
      ...rest
    } = props;

    // Runtime invariant — iconOnly without aria-label is an a11y bug.
    // Warn once in development; ship the bug visible to QA rather than silent.
    if (
      process.env.NODE_ENV !== 'production' &&
      iconOnly &&
      !ariaLabel
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Button] iconOnly buttons must provide an aria-label for screen readers.',
      );
    }

    const cls = classes(variant, size, iconOnly, fullWidth, loading, className);
    const isDisabled = disabled || loading;

    // Spinner replaces leftIcon (or sits in the leftIcon slot if no icon was
    // provided) — this avoids a layout shift when toggling loading.
    const left = loading ? <Spinner /> : leftIcon;

    const labelNode = !iconOnly && children ? (
      <span className="kk-button__label">{children}</span>
    ) : null;

    if (props.as === 'a') {
      const { as: _as, href, ...anchorRest } = rest as Omit<
        ButtonAsAnchorProps,
        keyof CommonProps
      >;
      // When rendered as an anchor, "disabled" + "loading" are advisory; we
      // emulate via aria-disabled + tabIndex=-1 + click suppression.
      const onClickGuard = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        const orig = (anchorRest as AnchorHTMLAttributes<HTMLAnchorElement>)
          .onClick;
        if (orig) orig(e);
      };
      return (
        <a
          {...(anchorRest as AnchorHTMLAttributes<HTMLAnchorElement>)}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          href={(props as ButtonAsAnchorProps).href}
          className={cls}
          aria-label={ariaLabel}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-disabled={isDisabled || undefined}
          aria-busy={loading || undefined}
          tabIndex={isDisabled ? -1 : undefined}
          data-testid={dataTestId}
          onClick={onClickGuard}
        >
          {left}
          {labelNode}
          {!loading && rightIcon}
        </a>
      );
    }

    const buttonRest = rest as Omit<ButtonAsButtonProps, keyof CommonProps>;
    const buttonType =
      (buttonRest as ButtonHTMLAttributes<HTMLButtonElement>).type ?? 'button';

    const onClickGuard = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const orig = (buttonRest as ButtonHTMLAttributes<HTMLButtonElement>)
        .onClick;
      if (orig) orig(e);
    };

    return (
      <button
        {...(buttonRest as ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        type={buttonType}
        className={cls}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        data-testid={dataTestId}
        onClick={onClickGuard}
      >
        {left}
        {labelNode}
        {!loading && rightIcon}
      </button>
    );
  },
);

export default Button;
